/// /apps/patient/src/components/navigation/Sidebar.tsx

import { Link, useLocation } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { usePatient } from '../../contexts/PatientContext';
import { useFeatures } from '../../contexts/FeatureContext';
import { getNavigationItems, getSidebarSections } from '../../config/navigationConfig';
import { cn } from '../../lib/utils';

interface SidebarProps {
  counts?: {
    unreadMessages?: number;
    upcomingAppointments?: number;
    activePrograms?: number;
  };
}

export function Sidebar({ counts = {} }: SidebarProps) {
  const location = useLocation();
  const { patient, logout } = usePatient();
  const { features, isLoading, isStandalone, isExercisePlatform } = useFeatures();

  if (isLoading || !features) {
    return (
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="flex flex-col w-64 bg-white border-r border-gray-200">
          <div className="h-16 flex items-center px-4 border-b">
            <div className="w-32 h-8 bg-gray-200 animate-pulse rounded" />
          </div>
        </div>
      </div>
    );
  }

  // Determine platform for navigation grouping
  // Exercise platform users see "Training" section, others see "Programs"
  const platform = isExercisePlatform ? 'exercise' : 'ehr';
  const allItems = getNavigationItems(features.enabled_features, counts, platform);
  const sections = getSidebarSections(allItems, platform);

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  return (
    <div className="hidden lg:flex lg:flex-shrink-0">
      <div className="flex flex-col w-64 bg-white border-r border-gray-200">
        {/* Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b">
          {isStandalone ? (
            <h1 className="text-xl font-bold text-primary">Ready2Exercise</h1>
          ) : (
            <div className="flex items-center space-x-2">
              <img 
                src="/prism-logo.png" 
                alt="Prism" 
                className="h-8 w-8"
              />
              <h1 className="text-xl font-bold text-primary">PrismEHR</h1>
            </div>
          )}
        </div>

        {/* User info */}
        <div className="px-4 py-4 border-b">
          <div className="flex items-center">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-primary font-semibold">
                {patient?.first_name?.[0]?.toUpperCase() || 'P'}
              </span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-900">
                {patient?.first_name} {patient?.last_name}
              </p>
              <p className="text-xs text-gray-500">
                {isStandalone ? 'Free Plan' : 'Patient'}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation sections */}
        <nav className="flex-1 px-2 py-4 space-y-6 overflow-y-auto">
          {sections.map((section) => (
            <div key={section.label}>
              <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {section.label}
              </h3>
              <div className="mt-2 space-y-1">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={cn(
                        "group flex items-center px-3 py-2 text-sm font-medium rounded-md relative",
                        isActive
                          ? "bg-primary/10 text-primary"
                          : "text-gray-700 hover:bg-gray-50"
                      )}
                    >
                      <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
                      <span className="flex-1">{item.label}</span>
                      {item.badge && item.badge > 0 && (
                        <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Logout button */}
        <div className="p-4 border-t">
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md"
          >
            <LogOut className="mr-3 h-5 w-5" />
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}